import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from 'recharts';
import supabaseClient from '@/api/supabaseClient.ts';
import { calculateSubdomainScore, getMaxSubdomainScore, getLevelName, SubdomainWithWeights, UserResponse } from '@/utils/assessmentUtils';

interface SubdomainChartData {
  name: string;
  domainName: string;
  score: number;
  maxScore: number;
  percentage: number;
  selectedLevel: number;
}

const getScoreColor = (percentage: number): string => {
  if (percentage >= 70) return '#6d9c40'; 
  if (percentage >= 40) return '#9aba7b'; 
  return '#e6bac8'; 
};

const useWindowSize = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

const exportSvgInContainerToPng = async (
  container: HTMLElement,
  filename: string
): Promise<void> => {
  const svg = container.querySelector('svg');
  if (!svg) return;

  const serializer = new XMLSerializer();
  const svgString = serializer.serializeToString(svg);

  const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(svgBlob);

  const img = new Image();
  const width = (svg as SVGSVGElement).clientWidth || 1200;
  const height = (svg as SVGSVGElement).clientHeight || 800;

  const scale = Math.max(2, Math.floor(window.devicePixelRatio || 2));
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    URL.revokeObjectURL(url);
    return;
  }
  ctx.scale(scale, scale);

  await new Promise<void>((resolve) => {
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);

      URL.revokeObjectURL(url);
      const link = document.createElement('a');
      const safeName = filename ? filename.replace(/[^a-z0-9-_]+/gi, '_') : 'assessment_chart';
      link.download = `${safeName}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      resolve();
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve();
    };

    img.src = url;
  });
};

const AssessmentChart: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userAssessmentId } = useParams<{ userAssessmentId: string }>();
  const isMobile = useWindowSize();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  
  const [chartData, setChartData] = useState<SubdomainChartData[]>([]);
  const [loading, setLoading] = useState(true);
  const [assessmentName, setAssessmentName] = useState<string>('');

  const fetchAssessmentResults = useCallback(async () => {
    try {
      setLoading(true);

      const { data: assessmentData, error: assessmentError } = await supabaseClient
        .from('user_assessments')
        .select('assessment_name')
        .eq('id', userAssessmentId)
        .maybeSingle();

      if (assessmentError || !assessmentData) {
        console.error('Error fetching assessment:', assessmentError);
        return;
      }

      setAssessmentName(assessmentData.assessment_name);

      const { data: responsesData, error: responsesError } = await supabaseClient
        .from('user_responses')
        .select(`
          selected_level,
          subdomain_id,
          subdomains (
            id,
            name,
            domain_id,
            discovery_weight,
            foundational_weight,
            emerging_weight,
            developing_weight,
            advancing_weight,
            thriving_weight,
            domains (
              id,
              name
            )
          )
        `)
        .eq('user_assessment_id', userAssessmentId);

      if (responsesError) {
        console.error('Error fetching responses:', responsesError);
        return;
      }

      if (!responsesData || responsesData.length === 0) {
        console.error('No responses found');
        return;
      }

      const typedResponses = (responsesData ?? []) as unknown as UserResponse[];

      let chartData: SubdomainChartData[] = typedResponses.map((response: UserResponse) => {
        const subdomain = response.subdomains as SubdomainWithWeights & {
          domains: { id: number; name: string; };
        };
        const selectedLevel = response.selected_level;
        
        const score = calculateSubdomainScore(subdomain, selectedLevel);
        const maxScore = getMaxSubdomainScore(subdomain);
        const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;

        return {
          name: subdomain.name,
          domainName: subdomain.domains.name,
          score: score,
          maxScore: maxScore,
          percentage: percentage,
          selectedLevel: selectedLevel
        };
      });

      chartData = chartData.sort((a, b) => {
        const domainComparison = a.domainName.localeCompare(b.domainName);
        if (domainComparison !== 0) {
          return domainComparison;
        }
        return a.name.localeCompare(b.name);
      });

      setChartData(chartData);

    } catch (err) {
      console.error('Error fetching assessment results:', err);
    } finally {
      setLoading(false);
    }
  }, [userAssessmentId]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleDownloadPNG = async () => {
    try {
      const container = chartContainerRef.current;
      if (!container) return;
      await exportSvgInContainerToPng(container, assessmentName);
    } catch (error) {
      console.error('Error exporting chart to PNG:', error);
    }
  };

  useEffect(() => {
    if (userAssessmentId) {
      fetchAssessmentResults();
    }
  }, [userAssessmentId, fetchAssessmentResults]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-semibold">{t('assessment.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-2xl">
      <div className="bg-white px-4 sm:px-6 py-4 shadow-sm rounded-2xl">
        <div className="flex flex-col sm:flex-row gap-4 sm:gap-0 justify-between items-center">
          <button
            onClick={handleGoBack}
            className="w-full sm:w-auto bg-gray-800 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors print:hidden"
          >
            ← {t('common.goBack')}
          </button>
          
          <h1 className="text-lg sm:text-xl font-bold text-gray-800 text-center">{assessmentName}</h1>

          <div className="hidden sm:flex items-center gap-2 print:hidden">
            <button
              onClick={handleDownloadPNG}
              className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-400 transition-colors items-center justify-center"
            >
              Download PNG
            </button>
            <button
              onClick={() => window.print()}
              className="w-full sm:w-auto bg-[#6d9c40] text-white px-4 py-2 rounded-lg hover:bg-[#5a8235] transition-colors items-center justify-center"
            >
              Print PDF
            </button>
          </div>
        </div>
      </div>

      <div ref={chartContainerRef} className="h-[calc(100vh-200px)] sm:h-[calc(100vh-80px)] p-2 sm:p-6" style={{ backgroundColor: 'var(--fallback-b2, oklch(var(--b2) / var(--tw-bg-opacity)))' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={chartData}
            margin={{
              top: isMobile ? 10 : 20,
              right: isMobile ? 10 : 40,
              left: isMobile ? 0 : 40,
              bottom: isMobile ? 80 : 120
            }}
            barCategoryGap={isMobile ? "10%" : "20%"}
          >
            <XAxis 
              dataKey="name"
              angle={isMobile ? -60 : -45}
              textAnchor="end"
              height={isMobile ? 80 : 120}
              fontSize={isMobile ? 8 : 12}
              interval={0}
              tick={{ 
                fontSize: isMobile ? '8px' : '12px',
                width: isMobile ? '80px' : '100px',
                dy: isMobile ? 8 : 0
              }}
            />
            <YAxis 
              domain={[0, 6]}
              ticks={[1, 2, 3, 4, 5, 6]}
              tickFormatter={(value) => getLevelName(Number(value))}
              fontSize={isMobile ? 10 : 12}
              width={isMobile ? 70 : 100}
            />
            <Bar 
              dataKey="selectedLevel" 
              fill="#6d9c40"
              radius={[4, 4, 0, 0]}
              maxBarSize={isMobile ? 20 : 40}
            >
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={getScoreColor(entry.percentage)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default AssessmentChart;